import { makeApiRequest } from '../utils/api';
import { resolveClientTransactionVersions } from './client-transaction-versions';
import { getConfig, isBoundedNetwork, isBoundedNetworkConfig } from './config';
import { runNamedQueriesOverSocket } from './subscription-v2';
import { getIdToken } from '../utils/utils';
import { safeBtoa } from '../utils/core-platform';
/**
 * Thrown when a user's wallet doesn't have enough SOL to cover the transaction.
 * Apps can catch this to trigger an onramp/funding flow.
 */
export class InsufficientBalanceError extends Error {
    constructor(address, balanceLamports, estimatedCostLamports, deficitLamports, deficitSol) {
        super(`Insufficient balance: wallet ${address} needs ~${deficitSol.toFixed(6)} SOL more to complete this transaction.`);
        this.name = 'InsufficientBalanceError';
        this.address = address;
        this.balanceLamports = balanceLamports;
        this.estimatedCostLamports = estimatedCostLamports;
        this.deficitLamports = deficitLamports;
        this.deficitSol = deficitSol;
    }
}
const getCache = {};
const pendingRequests = {};
// Default cache TTL (500ms = 0.5 seconds)
const GET_CACHE_TTL = 500; // Adjust this value as needed (in milliseconds)
function getReadCacheEntry(cacheKey, principalValue) {
    var _a;
    return (_a = getCache[cacheKey]) === null || _a === void 0 ? void 0 : _a.find(entry => entry.principalValue === principalValue);
}
function setReadCacheEntry(cacheKey, principalValue, data, expiresAt) {
    var _a;
    const bucket = (_a = getCache[cacheKey]) !== null && _a !== void 0 ? _a : [];
    const reusableEntries = bucket.filter(entry => (entry.principalValue !== principalValue && entry.expiresAt >= Date.now()));
    reusableEntries.push({ data, expiresAt, principalValue });
    getCache[cacheKey] = reusableEntries;
}
// Last time we cleaned up the cache
let lastCacheCleanup = Date.now();
let uncacheableReadKeyCounter = 0;
/**
 * Return the leaf document key (last path segment) for a document path.
 *
 * Bounded rows carry `_id` (and `pathId`) set to the FULL document path
 * (e.g. `"rooms/r1/prompts/8rd49se3sg"`). Apps that build a child path from a
 * row naturally want the bare doc key, not the whole path — using the full path
 * doubles it (`rooms/r1/prompts/rooms/r1/prompts/8rd.../votes/...`) → 403/404.
 * `docId` extracts that leaf key. Tolerates leading slashes and a trailing `*`.
 *
 * @example docId("rooms/r1/prompts/8rd49se3sg") // "8rd49se3sg"
 */
export function docId(path) {
    if (typeof path !== 'string')
        return '';
    let p = path.startsWith('/') ? path.slice(1) : path;
    if (p.endsWith('*') && p.length > 1)
        p = p.slice(0, -1);
    if (p.endsWith('/'))
        p = p.slice(0, -1);
    const segments = p.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
}
/**
 * Additively attach a convenience bare `id` (the leaf doc key) to a returned row.
 *
 * Rows already carry `_id`/`pathId` = the full document path; this leaves those
 * untouched and adds `id` = the last path segment so `row.id` is the doc key apps
 * expect. No-ops for non-objects, arrays, and rows that already define `id`
 * (never clobber a user field named `id`). Source for the leaf is `_id` (falling
 * back to `pathId`/`relativePath`/`absolutePath`).
 */
function withBareId(row) {
    var _a, _b, _c;
    if (!row || typeof row !== 'object' || Array.isArray(row))
        return row;
    const r = row;
    if ('id' in r)
        return row; // don't clobber an explicit user field
    const source = (_c = (_b = (_a = r._id) !== null && _a !== void 0 ? _a : r.pathId) !== null && _b !== void 0 ? _b : r.relativePath) !== null && _c !== void 0 ? _c : r.absolutePath;
    if (typeof source !== 'string' || source.length === 0)
        return row;
    const leaf = docId(source);
    if (!leaf)
        return row;
    return Object.assign(Object.assign({}, r), { id: leaf });
}
/**
 * Normalize a raw read response from the worker into the SDK's stable shape and
 * attach the bare `id` to every returned row (Bug 1 + Bug 2).
 *
 * - Single-document path: returns EXACTLY ONE shape — the resolved document, or
 *   `null` if missing (Firebase/Mongo convention). Unwraps the ambiguous
 *   `{ data, status }` envelope the worker sometimes emits for a single doc so a
 *   single-doc `get` never sometimes-returns the doc and sometimes the envelope.
 * - Collection path: preserves the `{ data, nextCursor, ... }` envelope and maps
 *   the bare `id` onto each row in `data`.
 */
function normalizeReadResult(responseData, pathIsDocument) {
    if (pathIsDocument) {
        let doc = responseData;
        // Unwrap the single-doc envelope `{ data, status }` (Bug 2). A real document
        // never has the exact shape `{ data, status }` with nothing else meaningful,
        // so detect the envelope by `data` + numeric `status` AND the absence of a
        // top-level `_id` (a real stored doc always carries `_id`/`pathId` from the
        // worker; the envelope wrapper does not). This avoids unwrapping a genuine
        // document that merely happens to own `data`/`status` fields.
        if (doc && typeof doc === 'object' && !Array.isArray(doc) &&
            'data' in doc && 'status' in doc && typeof doc.status === 'number' &&
            !('_id' in doc) && !('pathId' in doc)) {
            doc = doc.data;
        }
        if (doc === undefined || doc === null)
            return null;
        return withBareId(doc);
    }
    // Collection read. The worker body is `{ data: rows[], nextCursor?, status? }`.
    if (responseData && typeof responseData === 'object' && !Array.isArray(responseData) && Array.isArray(responseData.data)) {
        return Object.assign(Object.assign({}, responseData), { data: responseData.data.map((row) => withBareId(row)) });
    }
    // Some paths return a bare array of rows — map id onto each.
    if (Array.isArray(responseData)) {
        return responseData.map((row) => withBareId(row));
    }
    return responseData;
}
function hashForKey(value) {
    let h = 5381;
    for (let i = 0; i < value.length; i++) {
        h = ((h << 5) + h + value.charCodeAt(i)) & 0x7fffffff;
    }
    return h.toString(36);
}
// Memoize the compact principal hash per exact credential string. Every read
// hashed the entire (~1KB) bearer/idToken on every call; a session presents
// the same handful of credentials for its lifetime. Bounded size — a rotation
// simply inserts the new token and stale entries fall off on clear.
const principalHashCache = new Map();
function hashForKeyCached(value) {
    const hit = principalHashCache.get(value);
    if (hit !== undefined)
        return hit;
    const h = hashForKey(value);
    if (principalHashCache.size >= 50)
        principalHashCache.clear();
    principalHashCache.set(value, h);
    return h;
}
// #022: HTTP header names are case-insensitive on the wire, so recognize `authorization`
// in ANY casing when deriving the per-user cache key. Otherwise a caller-supplied
// `AUTHORIZATION: <token B>` rides into the request while the cache key is computed from
// the ambient token A - filing B's data under A's identity. The cache key must always be
// derived from the same credential the request authenticates with.
function authHeaderEntry(headers) {
    if (!headers)
        return undefined;
    for (const name of Object.keys(headers)) {
        if (name.toLowerCase() === 'authorization')
            return headers[name];
    }
    return undefined;
}
function hasAuthHeader(headers) {
    return authHeaderEntry(headers) !== undefined;
}
function authValueFromHeaders(headers) {
    return authHeaderEntry(headers) || '';
}
function principalFromAuthValue(authValue) {
    return authValue ? `h${hashForKeyCached(authValue)}` : null;
}
function principalFromIdToken(idToken) {
    return idToken ? `t${hashForKeyCached(idToken)}` : null;
}
function uncacheableReadKey(appId, scope) {
    uncacheableReadKeyCounter += 1;
    return `${appId}:${scope}-uncacheable-${uncacheableReadKeyCounter}`;
}
/**
 * SECURITY (H1): Read caches must be keyed by the caller's principal, not just
 * by path/filter/shape. In a shared process / SSR worker / browser login-switch,
 * keying by path alone lets User B receive User A's cached private read before
 * any server read rule runs. This returns `appId:<principal>` for the opaque
 * auth material a given read will actually authenticate with. No-auth reads are
 * deliberately marked uncacheable instead of sharing an implicit `anon` bucket.
 * JWTs are intentionally treated as opaque unverified bearer material — never
 * decoded claims and never caller identity hints such as `_walletAddress`.
 */
async function getReadPrincipalKey(overrides) {
    var _a;
    const config = (_a = overrides === null || overrides === void 0 ? void 0 : overrides._config) !== null && _a !== void 0 ? _a : await getConfig();
    const appId = config.appId || '';
    // A scoped read-only instance sends no auth and must never consult the ambient
    // global session for a principal - its reads are always uncacheable (no
    // per-user cache reuse, but also no cross-app/cross-user leak).
    if (overrides === null || overrides === void 0 ? void 0 : overrides._readOnly) {
        return { key: uncacheableReadKey(appId, 'ro'), cacheable: false, principalValue: null };
    }
    // makeApiRequest applies overrides.headers AFTER its computed auth header, so
    // caller-supplied Authorization is the real request auth when present.
    if (hasAuthHeader(overrides === null || overrides === void 0 ? void 0 : overrides.headers)) {
        const authValue = authValueFromHeaders(overrides === null || overrides === void 0 ? void 0 : overrides.headers);
        const principal = principalFromAuthValue(authValue);
        return principal
            ? { key: `${appId}:${principal}`, cacheable: true, principalValue: authValue }
            : { key: uncacheableReadKey(appId, 'h'), cacheable: false, principalValue: null };
    }
    // Per-request auth-header override (wallet client). Key by the exact opaque
    // header it produces, not decoded claims or the unverified _walletAddress hint.
    if (overrides === null || overrides === void 0 ? void 0 : overrides._getAuthHeaders) {
        try {
            const headers = await overrides._getAuthHeaders();
            const authValue = authValueFromHeaders(headers);
            const principal = principalFromAuthValue(authValue);
            return principal
                ? { key: `${appId}:o${principal}`, cacheable: true, principalValue: authValue }
                : { key: uncacheableReadKey(appId, 'o'), cacheable: false, principalValue: null };
        }
        catch (_b) {
            // If we can't resolve the override identity, do not read/write cache.
            return { key: uncacheableReadKey(appId, 'o'), cacheable: false, principalValue: null };
        }
    }
    // Ambient session principal.
    const idToken = await getIdToken(config.isServer);
    const principal = principalFromIdToken(idToken);
    return principal
        ? { key: `${appId}:${principal}`, cacheable: true, principalValue: idToken !== null && idToken !== void 0 ? idToken : null }
        : { key: uncacheableReadKey(appId, 'a'), cacheable: false, principalValue: null };
}
/**
 * Validates that a field name is a safe identifier (alphanumeric, underscores, dots for nested paths).
 * Prevents prompt injection via crafted field names.
 */
function validateFieldName(field) {
    if (!/^[a-zA-Z0-9_.]+$/.test(field)) {
        throw new Error(`Invalid field name "${field}". Field names must only contain letters, numbers, underscores, and dots.`);
    }
}
/**
 * Parses a raw aggregation result (e.g. [{ count: 42 }] or [{ _id: null, total: 100 }])
 * into a single numeric value.
 */
function parseAggregateValue(result) {
    if (typeof result === 'number')
        return result;
    if (Array.isArray(result)) {
        if (result.length === 0)
            return 0;
        // Multiple elements — not a collapsed aggregate result
        if (result.length > 1) {
            throw new Error(`Unexpected aggregate result: got array with ${result.length} elements. The AI may have returned full documents instead of an aggregation.`);
        }
        const first = result[0];
        if (typeof first === 'number')
            return first;
        if (first && typeof first === 'object') {
            // $count stage returns { count: N }
            if (typeof first.count === 'number')
                return first.count;
            // $group stage returns { _id: null, result: N } — expect _id + one numeric field
            const numericEntries = Object.entries(first).filter(([key, val]) => key !== '_id' && typeof val === 'number');
            if (numericEntries.length === 1)
                return numericEntries[0][1];
        }
        // Avoid leaking document contents into error messages
        const shape = first && typeof first === 'object' ? `{${Object.keys(first).join(', ')}}` : String(first);
        throw new Error(`Unexpected aggregate result shape: ${shape}. Expected {count: N} or {_id: null, <field>: N}.`);
    }
    if (result && typeof result === 'object' && typeof result.count === 'number') {
        return result.count;
    }
    throw new Error(`Unexpected aggregate result type: ${typeof result}. Expected a number, array, or object with a count field.`);
}
/**
 * Count items in a collection path. Returns a numeric result.
 *
 * This uses the AI query engine with a count-specific prompt prefix,
 * so Bounded will generate a $count aggregation pipeline and return
 * just the count rather than full documents.
 *
 * IMPORTANT: This only works for collections where the read policy is "true".
 * If the read policy requires per-document checks, the server will return
 * an error because aggregate counts cannot be performed without pulling all
 * documents for access control evaluation.
 *
 * @param path - Collection path (e.g., "posts", "users/abc/comments")
 * @param opts - Optional filter prompt and overrides
 * @returns AggregateResult with the count value
 */
/** Whether these opts target a Bounded network - honoring a scoped instance's
 *  own config (`_overrides._config`) rather than the module global. */
function optsTargetBounded(overrides) {
    return (overrides === null || overrides === void 0 ? void 0 : overrides._config) ? isBoundedNetworkConfig(overrides._config) : isBoundedNetwork();
}
export async function count(path, opts = {}) {
    var _a, _b;
    // On Bounded, the worker has no AI query engine — a prompt-based count returns
    // the raw documents, not an aggregation. Use the deterministic server
    // aggregation (queryAggregate) instead.
    if (optsTargetBounded(opts._overrides)) {
        const rows = await queryAggregate(path, { count: true }, { filter: opts.filter, _overrides: opts._overrides });
        return { value: (_b = (_a = rows[0]) === null || _a === void 0 ? void 0 : _a.count) !== null && _b !== void 0 ? _b : 0 };
    }
    const prefix = 'Return ONLY the total count of matching documents. Use the $count stage to produce a field named "count". Do NOT return the documents themselves.';
    const fullPrompt = opts.prompt
        ? `${prefix} Filter: ${opts.prompt}`
        : prefix;
    const result = await get(path, { prompt: fullPrompt, bypassCache: true, _overrides: opts._overrides });
    return { value: parseAggregateValue(result) };
}
/**
 * Bounded-native implementation of `aggregate`: map the single-scalar operation
 * onto the deterministic `queryAggregate` server aggregation and pull the value
 * out of the (single, ungrouped) result row. Empty collection → 0.
 */
async function boundedAggregate(path, operation, opts) {
    var _a, _b, _c;
    const qopts = { filter: opts.filter, _overrides: opts._overrides };
    if (operation === 'count') {
        const rows = await queryAggregate(path, { count: true }, qopts);
        return (_b = (_a = rows[0]) === null || _a === void 0 ? void 0 : _a.count) !== null && _b !== void 0 ? _b : 0;
    }
    if (!opts.field)
        throw new Error(`aggregate "${operation}" requires a field option`);
    validateFieldName(opts.field);
    if (operation === 'uniqueCount') {
        // distinct values = number of groups when grouping by the field.
        const rows = await queryAggregate(path, { groupBy: [opts.field] }, qopts);
        return rows.length;
    }
    // sum / avg / min / max → one bucket keyed by the field on the single row.
    const rows = await queryAggregate(path, { [operation]: [opts.field] }, qopts);
    const bucket = (_c = rows[0]) === null || _c === void 0 ? void 0 : _c[operation];
    const v = bucket === null || bucket === void 0 ? void 0 : bucket[opts.field];
    return typeof v === 'number' ? v : 0;
}
/**
 * Run an aggregate operation on a collection path. Returns a numeric result.
 *
 * Supported operations:
 * - count: Total number of documents
 * - uniqueCount: Number of distinct values for a field
 * - sum: Sum of a numeric field
 * - avg: Average of a numeric field
 * - min: Minimum value of a numeric field
 * - max: Maximum value of a numeric field
 *
 * IMPORTANT: This only works for collections where the read policy is "true".
 * If the read policy requires per-document checks, the server will return
 * an error because aggregate operations cannot be performed without pulling
 * all documents for access control evaluation.
 *
 * @param path - Collection path (e.g., "posts", "users/abc/comments")
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregate(path, operation, opts = {}) {
    // On Bounded, route through the deterministic server aggregation — the legacy
    // AI-prompt path returns raw documents on the worker (no AI query engine).
    if (optsTargetBounded(opts._overrides)) {
        return { value: await boundedAggregate(path, operation, opts) };
    }
    let prefix;
    switch (operation) {
        case 'count':
            prefix = 'Return ONLY the total count of matching documents. Use the $count stage to produce a field named "count". Do NOT return the documents themselves.';
            break;
        case 'uniqueCount':
            if (!opts.field)
                throw new Error('aggregate "uniqueCount" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the count of unique/distinct values of the "${opts.field}" field. Use $group with _id set to "$${opts.field}" then $count to produce a field named "count". Do NOT return the documents themselves.`;
            break;
        case 'sum':
            if (!opts.field)
                throw new Error('aggregate "sum" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the sum of the "${opts.field}" field across all matching documents. Use $group with _id: null and result: { $sum: "$${opts.field}" }. Do NOT return the documents themselves.`;
            break;
        case 'avg':
            if (!opts.field)
                throw new Error('aggregate "avg" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the average of the "${opts.field}" field across all matching documents. Use $group with _id: null and result: { $avg: "$${opts.field}" }. Do NOT return the documents themselves.`;
            break;
        case 'min':
            if (!opts.field)
                throw new Error('aggregate "min" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the minimum value of the "${opts.field}" field across all matching documents. Use $group with _id: null and result: { $min: "$${opts.field}" }. Do NOT return the documents themselves.`;
            break;
        case 'max':
            if (!opts.field)
                throw new Error('aggregate "max" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the maximum value of the "${opts.field}" field across all matching documents. Use $group with _id: null and result: { $max: "$${opts.field}" }. Do NOT return the documents themselves.`;
            break;
        default:
            throw new Error(`Unsupported aggregate operation: ${operation}`);
    }
    const fullPrompt = opts.prompt
        ? `${prefix} Filter: ${opts.prompt}`
        : prefix;
    const result = await get(path, { prompt: fullPrompt, bypassCache: true, _overrides: opts._overrides });
    // For uniqueCount, the AI may return $group results without the final $count
    // stage, producing [{_id: "val1"}, {_id: "val2"}, ...]. Verify elements look
    // like $group output (only _id key) before using array length as the count.
    if (operation === 'uniqueCount' && Array.isArray(result) && result.length > 1) {
        const looksLikeGroupOutput = result.every((el) => el && typeof el === 'object' && Object.keys(el).length === 1 && '_id' in el);
        if (looksLikeGroupOutput) {
            return { value: result.length };
        }
        throw new Error(`Unexpected uniqueCount result: got ${result.length} elements that don't match $group output shape.`);
    }
    return { value: parseAggregateValue(result) };
}
/**
 * Structured, grouped aggregation over a collection. Returns one row per group
 * (or a single row when `spec.groupBy` is omitted). Read rules are enforced — the
 * aggregation only sees documents the caller can read.
 *
 * ```ts
 * const byCat = await queryAggregate("spend", { groupBy: ["category"], count: true, sum: ["amount"] });
 * // [{ group: { category: "food" }, count: 2, sum: { amount: 70 } }, ...]
 * ```
 */
export async function queryAggregate(path, spec, opts = {}) {
    var _a, _b, _c, _d, _e;
    let normalizedPath = path.startsWith("/") ? path.slice(1) : path;
    if (normalizedPath.endsWith("*") && normalizedPath.length > 1) {
        normalizedPath = normalizedPath.slice(0, -1);
    }
    if (!normalizedPath || normalizedPath.length === 0) {
        throw new Error("Invalid path provided.");
    }
    // Validate every referenced field name to prevent injection via crafted keys.
    // Each of these takes an ARRAY of field names — catch the common mistake of
    // passing an object/string (e.g. `sum: { field: "n" }`) with a clear error
    // instead of a cryptic "list is not iterable" from the for-of below.
    for (const key of ['groupBy', 'sum', 'avg', 'min', 'max']) {
        const list = spec[key];
        if (list == null)
            continue;
        if (!Array.isArray(list)) {
            throw new Error(`queryAggregate: spec.${key} must be an array of field names ` +
                `(e.g. ${key}: ["amount"]), got ${typeof list}`);
        }
        for (const f of list)
            validateFieldName(f);
    }
    const cleanSpec = {};
    if ((_a = spec.groupBy) === null || _a === void 0 ? void 0 : _a.length)
        cleanSpec.groupBy = spec.groupBy;
    if (spec.count)
        cleanSpec.count = true;
    if ((_b = spec.sum) === null || _b === void 0 ? void 0 : _b.length)
        cleanSpec.sum = spec.sum;
    if ((_c = spec.avg) === null || _c === void 0 ? void 0 : _c.length)
        cleanSpec.avg = spec.avg;
    if ((_d = spec.min) === null || _d === void 0 ? void 0 : _d.length)
        cleanSpec.min = spec.min;
    if ((_e = spec.max) === null || _e === void 0 ? void 0 : _e.length)
        cleanSpec.max = spec.max;
    const aggParam = `&aggregate=${encodeURIComponent(JSON.stringify(cleanSpec))}`;
    const filterParam = opts.filter ? `&filter=${encodeURIComponent(JSON.stringify(opts.filter))}` : '';
    const apiPath = `items?path=${encodeURIComponent(normalizedPath)}${aggParam}${filterParam}`;
    const response = await makeApiRequest('GET', apiPath, null, opts._overrides);
    const body = response.data;
    // Worker returns { data: AggregateRow[], nextCursor, status } for aggregate queries.
    if (Array.isArray(body))
        return body;
    if (body && Array.isArray(body.data))
        return body.data;
    return [];
}
/**
 * Full-text search a collection declared with `search: { fields: [...] }`.
 *
 * The match runs over the collection's indexed fields (or the subset passed in
 * `opts.fields`) and respects each document's `read` rule — results the caller
 * cannot read are omitted. Returns the matching documents (optionally paged via
 * `opts.limit`/`opts.cursor`).
 *
 * @param path  Collection path, e.g. "orgs/o1/docs"
 * @param query Free-text query string (non-empty)
 */
export async function search(path, query, opts = {}) {
    let normalizedPath = path.startsWith("/") ? path.slice(1) : path;
    if (normalizedPath.endsWith("*") && normalizedPath.length > 1) {
        normalizedPath = normalizedPath.slice(0, -1);
    }
    if (!normalizedPath || normalizedPath.length === 0) {
        throw new Error("Invalid path provided.");
    }
    if (typeof query !== "string" || query.trim().length === 0) {
        throw new Error("search query must be a non-empty string");
    }
    if (opts.fields) {
        for (const f of opts.fields)
            validateFieldName(f);
    }
    const spec = { query };
    if (opts.fields && opts.fields.length > 0)
        spec.fields = opts.fields;
    const searchParam = `&search=${encodeURIComponent(JSON.stringify(spec))}`;
    const limitParam = opts.limit !== undefined ? `&limit=${opts.limit}` : '';
    const cursorParam = opts.cursor ? `&cursor=${encodeURIComponent(opts.cursor)}` : '';
    const apiPath = `items?path=${encodeURIComponent(normalizedPath)}${searchParam}${limitParam}${cursorParam}`;
    const response = await makeApiRequest('GET', apiPath, null, opts._overrides);
    return response.data;
}
export async function get(path, opts = {}) {
    var _a, _b, _c;
    try {
        let normalizedPath = path.startsWith("/") ? path.slice(1) : path;
        // Remove last '*' if it exists in the path
        if (normalizedPath.endsWith("*") && normalizedPath.length > 1) {
            normalizedPath = normalizedPath.slice(0, -1);
        }
        if (!normalizedPath || normalizedPath.length === 0) {
            throw new Error("Invalid path provided.");
        }
        // Create cache key combining path, prompt, filter, sort, includeSubPaths,
        // shape, limit, cursor — and (H1) the caller's appId + compact principal
        // bucket. Exact opaque credential equality is checked inside that bucket,
        // so a private read cached for one user is never served to another in a
        // shared process / SSR worker / browser login-switch. The cache is opt-in
        // and disabled for no-auth reads, which get an uncacheable unique key.
        const shapeKey = opts.shape ? JSON.stringify(opts.shape) : '';
        const includeSubPathsKey = opts.includeSubPaths ? ':subpaths' : '';
        const limitKey = opts.limit !== undefined ? `:l${opts.limit}` : '';
        const cursorKey = opts.cursor ? `:c${hashForKey(opts.cursor)}` : '';
        const filterKey = opts.filter ? `:f${hashForKey(JSON.stringify(opts.filter))}` : '';
        const sortKey = opts.sort ? `:s${hashForKey(JSON.stringify(opts.sort))}` : '';
        const principalKey = await getReadPrincipalKey(opts._overrides);
        const cacheKey = `${principalKey.key}|${normalizedPath}:${opts.prompt || ''}${filterKey}${sortKey}${includeSubPathsKey}:${shapeKey}${limitKey}${cursorKey}`;
        const cacheEnabled = opts.cache === true && !opts.bypassCache && principalKey.cacheable;
        const now = Date.now();
        // Check for valid cache entry when the caller explicitly opted in.
        const cachedEntry = getReadCacheEntry(cacheKey, principalKey.principalValue);
        if (cacheEnabled &&
            cachedEntry &&
            now < cachedEntry.expiresAt) {
            return cachedEntry.data;
        }
        // If we're bypassing cache, we should still coalesce identical requests
        // but need a different key to avoid interfering with regular requests.
        // Abortable calls are deliberately never coalesced: sharing one Axios
        // promise would let an old route's AbortSignal cancel a newer consumer of
        // the same path, or make the newer consumer inherit the old cancellation.
        const requestKey = opts.bypassCache ? `bypass:${cacheKey}` : cacheKey;
        const shouldCoalesce = !((_a = opts._overrides) === null || _a === void 0 ? void 0 : _a.signal);
        // The compact principal hash is only an index bucket. Coalesce only when
        // the exact opaque credential also matches, so collisions stay isolated.
        const existingPending = shouldCoalesce
            ? (_b = pendingRequests[requestKey]) === null || _b === void 0 ? void 0 : _b.find(entry => entry.principalValue === principalKey.principalValue)
            : undefined;
        if (existingPending) {
            return existingPending.promise;
        }
        // Create a new request promise and store it
        let requestPromise;
        requestPromise = (async () => {
            var _a;
            try {
                const pathIsDocument = normalizedPath.split("/").length % 2 === 0;
                // Cache miss or bypass - proceed with HTTP API request
                let response;
                // Build common query params
                const includeSubPathsParam = opts.includeSubPaths ? '&includeSubPaths=true' : '';
                const shapeParam = opts.shape ? `&shape=${encodeURIComponent(JSON.stringify(opts.shape))}` : '';
                const limitParam = opts.limit !== undefined ? `&limit=${opts.limit}` : '';
                const cursorParam = opts.cursor ? `&cursor=${encodeURIComponent(opts.cursor)}` : '';
                const filterParam = opts.filter ? `&filter=${encodeURIComponent(JSON.stringify(opts.filter))}` : '';
                const sortParam = opts.sort ? `&sort=${encodeURIComponent(JSON.stringify(opts.sort))}` : '';
                if (pathIsDocument) {
                    const itemId = encodeURIComponent(normalizedPath);
                    const queryParams = [includeSubPathsParam, shapeParam].filter(p => p).join('');
                    const apiPath = queryParams ? `items/${itemId}?${queryParams.substring(1)}` : `items/${itemId}`;
                    response = await makeApiRequest('GET', apiPath, null, opts._overrides);
                }
                else {
                    const path = encodeURIComponent(normalizedPath);
                    // encodeURIComponent the base64: safeBtoa emits standard base64 (with '+' '/' '='),
                    // and a raw '+' in a query string decodes to a space on the server
                    // (url.searchParams.get), corrupting the exact non-ASCII prompts DS2-0463 fixed. The
                    // server percent-decodes back to the original base64 before decodeUtf8Base64. (DS2-0463)
                    const promptQueryParam = (opts === null || opts === void 0 ? void 0 : opts.prompt) ? `&prompt=${encodeURIComponent(safeBtoa(opts.prompt))}` : "";
                    const apiPath = `items?path=${path}${promptQueryParam}${filterParam}${sortParam}${includeSubPathsParam}${shapeParam}${limitParam}${cursorParam}`;
                    response = await makeApiRequest('GET', apiPath, null, opts._overrides);
                }
                // Normalize the worker's raw body into the SDK's stable shape:
                //   - single-doc path → the resolved document or null (Bug 2), and
                //   - collection path → `{ data, nextCursor }` preserved,
                // with the bare `id` (leaf doc key) attached to every returned row (Bug 1).
                const responseData = normalizeReadResult(response.data, pathIsDocument);
                // Cache the response only when explicitly requested and principal-bound.
                if (cacheEnabled) {
                    setReadCacheEntry(cacheKey, principalKey.principalValue, responseData, now + GET_CACHE_TTL);
                    // Periodically clean up expired cache entries (every 5 seconds)
                    if (now - lastCacheCleanup > 5000) {
                        cleanupExpiredCache();
                        lastCacheCleanup = now;
                    }
                }
                // Return the data from the response
                return responseData;
            }
            finally {
                // Remove only this exact request. A colliding principal can legitimately
                // have another request in the same compact-hash bucket.
                if (shouldCoalesce) {
                    const bucket = pendingRequests[requestKey];
                    const index = (_a = bucket === null || bucket === void 0 ? void 0 : bucket.findIndex(entry => entry.promise === requestPromise)) !== null && _a !== void 0 ? _a : -1;
                    if (index >= 0)
                        bucket.splice(index, 1);
                    if ((bucket === null || bucket === void 0 ? void 0 : bucket.length) === 0)
                        delete pendingRequests[requestKey];
                }
            }
        })();
        // Store the promise so only callers with the exact same credential join it.
        if (shouldCoalesce) {
            ((_c = pendingRequests[requestKey]) !== null && _c !== void 0 ? _c : (pendingRequests[requestKey] = [])).push({
                principalValue: principalKey.principalValue,
                promise: requestPromise,
            });
        }
        // Return the promise
        return requestPromise;
    }
    catch (error) {
        throw error;
    }
}
// Helper to clean up expired cache entries
function cleanupExpiredCache() {
    const now = Date.now();
    Object.keys(getCache).forEach(key => {
        var _a, _b;
        const activeEntries = (_b = (_a = getCache[key]) === null || _a === void 0 ? void 0 : _a.filter(entry => entry.expiresAt >= now)) !== null && _b !== void 0 ? _b : [];
        if (activeEntries.length > 0)
            getCache[key] = activeEntries;
        else
            delete getCache[key];
    });
    lastCacheCleanup = now;
}
function classifyGetManyBatchError(error) {
    var _a, _b, _c;
    const err = error;
    const status = (_b = (_a = err === null || err === void 0 ? void 0 : err.status) !== null && _a !== void 0 ? _a : err === null || err === void 0 ? void 0 : err.statusCode) !== null && _b !== void 0 ? _b : (_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.status;
    const message = error instanceof Error
        ? error.message
        : typeof (err === null || err === void 0 ? void 0 : err.message) === 'string'
            ? err.message
            : 'Unknown error';
    if (status === 401 || status === 403) {
        return { code: 'UNAUTHORIZED', message };
    }
    if (status === 400) {
        return { code: 'INVALID_PATH', message };
    }
    return { code: 'REQUEST_FAILED', message };
}
export async function getMany(paths, opts = {}) {
    var _a, _b, _c, _d, _e;
    if (paths.length === 0) {
        return [];
    }
    if (paths.length > 30) {
        throw new Error('Cannot fetch more than 30 documents at once');
    }
    const normalizedPaths = [];
    for (const path of paths) {
        let normalizedPath = path.startsWith("/") ? path.slice(1) : path;
        if (normalizedPath.endsWith("*") && normalizedPath.length > 1) {
            normalizedPath = normalizedPath.slice(0, -1);
        }
        if (!normalizedPath || normalizedPath.length === 0) {
            throw new Error(`Invalid path provided: ${path}`);
        }
        const pathIsDocument = normalizedPath.split("/").length % 2 === 0;
        if (!pathIsDocument) {
            throw new Error(`Path must point to a document (even number of segments): ${path}`);
        }
        normalizedPaths.push(normalizedPath);
    }
    const now = Date.now();
    // H1: principal-scope getMany cache keys so one user's batch reads are never
    // served to another. Same `<appId:principal>|<path>:` shape used by get().
    const principalKey = await getReadPrincipalKey(opts._overrides);
    const cacheEnabled = opts.cache === true && !opts.bypassCache && principalKey.cacheable;
    const results = new Array(paths.length);
    const uncachedIndices = [];
    const uncachedPaths = [];
    for (let i = 0; i < normalizedPaths.length; i++) {
        const normalizedPath = normalizedPaths[i];
        const cacheKey = `${principalKey.key}|${normalizedPath}:`;
        const cachedEntry = getReadCacheEntry(cacheKey, principalKey.principalValue);
        if (cacheEnabled &&
            cachedEntry &&
            now < cachedEntry.expiresAt) {
            results[i] = { path: normalizedPath, data: cachedEntry.data };
        }
        else {
            uncachedIndices.push(i);
            uncachedPaths.push(normalizedPath);
        }
    }
    if (uncachedPaths.length > 0) {
        try {
            const response = await makeApiRequest('POST', 'items/batch', { paths: uncachedPaths }, opts._overrides);
            if (response.status === 404 && response.data == null) {
                const endpointError = new Error('Batch read endpoint returned 404');
                endpointError.status = 404;
                throw endpointError;
            }
            // makeApiRequest returns `{ data: <httpBody> }`, and the worker's items/batch
            // httpBody is `{ data: { results: [...] }, status }` — so the results are
            // double-nested at response.data.data.results. (Reading response.data.results
            // here is undefined → "serverResults is not iterable".) Tolerate both shapes.
            const serverResults = (_e = (_c = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.results) !== null && _c !== void 0 ? _c : (_d = response.data) === null || _d === void 0 ? void 0 : _d.results) !== null && _e !== void 0 ? _e : [];
            const serverResultsMap = new Map();
            for (const result of serverResults) {
                serverResultsMap.set(result.path, result);
            }
            for (let i = 0; i < uncachedIndices.length; i++) {
                const originalIndex = uncachedIndices[i];
                const normalizedPath = uncachedPaths[i];
                const serverResult = serverResultsMap.get(normalizedPath);
                if (serverResult) {
                    // getMany batches single-doc paths — attach the bare `id` (leaf doc
                    // key) to each returned doc, additive (Bug 1), matching get().
                    const normalizedResult = serverResult.error
                        ? serverResult
                        : Object.assign(Object.assign({}, serverResult), { data: withBareId(serverResult.data) });
                    results[originalIndex] = normalizedResult;
                    if (!normalizedResult.error && cacheEnabled) {
                        const cacheKey = `${principalKey.key}|${normalizedPath}:`;
                        setReadCacheEntry(cacheKey, principalKey.principalValue, normalizedResult.data, now + GET_CACHE_TTL);
                    }
                }
                else {
                    results[originalIndex] = {
                        path: normalizedPath,
                        data: null,
                        error: { code: 'NOT_FOUND', message: `No result returned for path ${normalizedPath}` }
                    };
                }
            }
            if (cacheEnabled && now - lastCacheCleanup > 5000) {
                cleanupExpiredCache();
                lastCacheCleanup = now;
            }
        }
        catch (error) {
            const batchError = classifyGetManyBatchError(error);
            for (const originalIndex of uncachedIndices) {
                results[originalIndex] = {
                    path: normalizedPaths[originalIndex],
                    data: null,
                    error: batchError,
                };
            }
        }
    }
    return results;
}
const MAX_RUN_QUERIES_PER_POST = 10;
let pendingRunQueries = [];
let runQueryFlushScheduled = false;
function unwrapRunQueryRow(row, entry) {
    var _a;
    // The worker disambiguates a computed null from a failed/undeclared query
    // with a per-row `error` string, so surface it instead of returning undefined.
    if ((row === null || row === void 0 ? void 0 : row.error) != null) {
        const err = new Error(String(row.error));
        err.isQueryError = true;
        err.queryName = entry.queryName;
        err.path = entry.absolutePath;
        throw err;
    }
    return (_a = row === null || row === void 0 ? void 0 : row.result) !== null && _a !== void 0 ? _a : null;
}
function flushPendingRunQueries() {
    const pending = pendingRunQueries;
    pendingRunQueries = [];
    runQueryFlushScheduled = false;
    // Calls with any request overrides bypass this buffer, so every entry here
    // belongs to the same default-overrides identity bucket.
    for (let offset = 0; offset < pending.length; offset += MAX_RUN_QUERIES_PER_POST) {
        const chunk = pending.slice(offset, offset + MAX_RUN_QUERIES_PER_POST);
        void runQueryManyRows(chunk.map(({ entry }) => entry)).then((rows) => {
            chunk.forEach((caller, index) => {
                try {
                    caller.resolve(unwrapRunQueryRow(rows[index], caller.entry));
                }
                catch (error) {
                    caller.reject(error);
                }
            });
        }, (error) => {
            chunk.forEach(({ reject }) => reject(error));
        });
    }
}
export async function runQuery(absolutePath, queryName, queryArgs, opts) {
    const entry = { absolutePath, queryName, queryArgs };
    // Auth, custom headers, timeout, and signal overrides must remain local to
    // one request, so any override-bearing call deliberately bypasses coalescing.
    if ((opts === null || opts === void 0 ? void 0 : opts._overrides) != null) {
        const rows = await runQueryManyRows([entry], opts);
        return unwrapRunQueryRow(rows[0], entry);
    }
    return new Promise((resolve, reject) => {
        pendingRunQueries.push({ entry, resolve, reject });
        if (!runQueryFlushScheduled) {
            runQueryFlushScheduled = true;
            queueMicrotask(flushPendingRunQueries);
        }
    });
}
export async function runQueryMany(many, opts) {
    // Batch shape stays an array of result values (errored rows map to null) so
    // existing callers keep working; use runQuery for per-row error surfacing.
    const rows = await runQueryManyRows(many, opts);
    return rows.map((row) => { var _a; return (_a = row === null || row === void 0 ? void 0 : row.result) !== null && _a !== void 0 ? _a : null; });
}
async function runQueryManyRows(many, opts) {
    var _a, _b, _c, _d;
    try {
        let queries = [];
        for (const { absolutePath, queryName, queryArgs } of many) {
            const normalizedPath = absolutePath.startsWith("/") ? absolutePath.slice(1) : absolutePath;
            const pathIsDocument = normalizedPath.split("/").length % 2 === 0;
            if (!pathIsDocument) {
                throw new Error(`Invalid path provided make sure it is pointing to a document: ${absolutePath}`);
            }
            queries.push({ path: normalizedPath, queryName, queryArgs });
        }
        // v2 socket-when-warm transport: when a warm, identity-matched app socket
        // already exists and the server advertised the runQuery capability, run this
        // batch over the socket instead of a fresh HTTP POST. Default-identity calls
        // only - per-request overrides (a distinct identity) always use HTTP. Any
        // miss/failure returns null and falls through to the identical HTTP call below,
        // so the result is byte-identical either way. Never opens a socket just to query.
        if ((opts === null || opts === void 0 ? void 0 : opts._overrides) == null) {
            const cfg = await getConfig();
            const socketRows = await runNamedQueriesOverSocket(cfg.appId, queries, cfg.isServer).catch(() => null);
            if (socketRows != null) {
                return socketRows;
            }
        }
        const response = await makeApiRequest('POST', 'queries', { queries }, opts === null || opts === void 0 ? void 0 : opts._overrides);
        // makeApiRequest returns `{ data: <httpBody> }`, and the worker's httpBody
        // is `{ data: { queries: [...] }, status }` — so the rows are double-nested
        // at response.data.data.queries. (Reading response.data.queries here is
        // undefined → "Cannot read properties of undefined".) Tolerate both shapes,
        // same as getMany does for items/batch.
        const rows = (_c = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.queries) !== null && _c !== void 0 ? _c : (_d = response.data) === null || _d === void 0 ? void 0 : _d.queries;
        if (!Array.isArray(rows)) {
            throw new Error(`queries endpoint returned an unexpected response shape (status ${response.status})`);
        }
        return rows;
    }
    catch (error) {
        throw error;
    }
}
// NEW: Ad-hoc expression execution - first-class feature
export async function runExpression(expression, queryArgs, options) {
    const results = await runExpressionMany([Object.assign({ expression, queryArgs }, options)]);
    return results[0];
}
export async function runExpressionMany(many) {
    var _a, _b, _c, _d;
    try {
        let queries = [];
        let firstCredential = undefined;
        let credentialSeen = false;
        for (const { expression, queryArgs, returnType, _overrides } of many) {
            // All batched queries ride ONE request, so they must run under ONE credential.
            // "Ambient" (no _overrides = the logged-in user) is a DISTINCT credential, not
            // a wildcard any override may satisfy: capture the FIRST entry's credential
            // state (including undefined/ambient) and reject any later entry whose
            // credential differs. Silently running the whole batch under the first
            // override found would evaluate a query meant for user B - or an ambient
            // end-user row under its restrictive policy - as whoever supplied an override
            // (e.g. an admin), a cross-principal authorization confusion the caller can't
            // detect (#103, DS2-0085). Split the batch by credential to run under
            // different identities.
            if (!credentialSeen) {
                firstCredential = _overrides;
                credentialSeen = true;
            }
            else if (_overrides !== firstCredential) {
                throw new Error("[bounded-sdk] runExpressionMany: batched queries carry different _overrides; all queries in one batch share a single request and credential. Split the batch so each request uses one credential.");
            }
            queries.push({ queryArgs, expression, returnType });
        }
        const response = await makeApiRequest('POST', 'queries', { queries }, firstCredential);
        // Same double-nesting as runQueryMany: the worker's httpBody is
        // { data: { queries: [...] }, status } under makeApiRequest's own
        // { data: <httpBody> } wrapper. Tolerate both shapes.
        const rows = (_c = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.queries) !== null && _c !== void 0 ? _c : (_d = response.data) === null || _d === void 0 ? void 0 : _d.queries;
        if (!Array.isArray(rows)) {
            throw new Error(`queries endpoint returned an unexpected response shape (status ${response.status})`);
        }
        // Map results - trace is automatically included by server for non-mainnet networks
        return rows.map((queryResult) => {
            const result = { result: queryResult.result };
            // Include trace if present (server includes it for non-mainnet networks)
            if (queryResult.trace) {
                result.trace = queryResult.trace;
            }
            // Pass through the server's per-row failure so callers can distinguish
            // a computed null from a failed row.
            if (queryResult.error != null) {
                result.error = String(queryResult.error);
            }
            return result;
        });
    }
    catch (error) {
        throw error;
    }
}
/* ===================== WRITE RECEIPT HELPERS (W4) ======================== */
/**
 * Settle delay before the best-effort mirror read on an on-chain write. It is
 * an optimization, not a correctness guarantee: the receipt is captured first
 * and returned regardless of what the mirror read reports.
 */
const MIRROR_SYNC_SETTLE_MS = 1500;
const SOLANA_CHAIN_TAGS = new Set(['solana_devnet', 'solana_mainnet']);
/**
 * Read the execution-lane tag the WORKER reported. The SDK never infers it: a
 * batch touching only unflagged collections in a `realtime_devnet` app commits
 * offchain, so the app's protocol is not the lane. Several field names are
 * accepted (batch-level receipt first, then the per-transaction Solana
 * `network` tag that predates it), but no value is ever manufactured.
 */
function readWorkerChainTag(...sources) {
    for (const source of sources) {
        if (!source || typeof source !== 'object')
            continue;
        for (const key of ['chain', 'executionChain', 'network']) {
            const value = source[key];
            if (typeof value === 'string' && value.trim().length > 0)
                return value.trim();
        }
    }
    return null;
}
/** Path-keyed echo of what the caller asked for. NEVER authoritative state. */
function buildRequestedDocuments(documents) {
    return documents.map(entry => {
        var _a;
        return ({
            path: entry.destinationPath,
            document: ((_a = entry.document) !== null && _a !== void 0 ? _a : null),
        });
    });
}
/** Look a concrete path up in a path-keyed map, tolerating a leading slash. */
function pickPathDocument(source, path) {
    if (Object.prototype.hasOwnProperty.call(source, path))
        return source[path];
    const withSlash = path.startsWith('/') ? path : `/${path}`;
    if (Object.prototype.hasOwnProperty.call(source, withSlash))
        return source[withSlash];
    const withoutSlash = path.startsWith('/') ? path.slice(1) : path;
    if (Object.prototype.hasOwnProperty.call(source, withoutSlash))
        return source[withoutSlash];
    return undefined;
}
/**
 * `observed: true` only when EVERY requested concrete path was read back as a
 * real document. The sync endpoint answers `null` for a document that is
 * absent, lagging, or read-denied, and a deleted path reads back as `null` too
 * — none of those are evidence of synchronization.
 */
function validatePathKeyedDocuments(paths, source) {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
        return { observed: false, reason: 'the synchronization response was not a path-keyed document map' };
    }
    const map = source;
    const documents = [];
    const unobserved = [];
    for (const path of paths) {
        const document = pickPathDocument(map, path);
        if (document == null || typeof document !== 'object' || Array.isArray(document)) {
            unobserved.push(path);
            documents.push({ path, document: null });
            continue;
        }
        documents.push({ path, document: document });
    }
    if (unobserved.length > 0) {
        return {
            observed: false,
            reason: `no readable document was returned for ${unobserved.join(', ')} ` +
                '(absent, deleted, lagging, read-denied, or a passthrough write)',
        };
    }
    return { observed: true, documents };
}
/**
 * Best-effort mirror read. A confirmed transaction must NEVER be lost to a
 * synchronization failure, so this reports its outcome in `mirrorSync` and
 * never rejects. It reads the CONCRETE destination paths, so a collection
 * insert with a generated id syncs the path that actually exists.
 */
export async function attemptMirrorSync(paths, options, settleMs = MIRROR_SYNC_SETTLE_MS) {
    try {
        if (settleMs > 0)
            await new Promise(resolve => setTimeout(resolve, settleMs));
        const synced = await syncItems(paths, options);
        // The sync endpoint answers `{ data: <path-keyed map>, status }`; a document
        // path always contains a `/`, so a top-level `data` key is never one.
        const map = (synced && typeof synced === 'object' && !Array.isArray(synced) && 'data' in synced)
            ? synced.data
            : synced;
        return validatePathKeyedDocuments(paths, map);
    }
    catch (error) {
        const message = typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' ? error.message : String(error);
        return { observed: false, reason: `mirror synchronization failed: ${message}` };
    }
}
function normalizePathKey(path) {
    return path.startsWith('/') ? path.slice(1) : path;
}
/**
 * Post-hook observation for a DIRECT commit, taken from the worker's own batch
 * receipt. The worker is the only party that can read every concrete path back
 * AFTER hooks have run, so when it reports nothing we say so rather than
 * echoing the request as if it were server state.
 *
 * Unlike the mirror read, a `null` document here IS authoritative: the worker
 * read its own store, so a deleted path genuinely is absent. The worker's
 * `observed` verdict is still re-validated against the requested paths — a
 * receipt that skips one of them cannot be treated as complete.
 */
function observationFromDirectCommit(paths, observation) {
    var _a;
    if (!observation || typeof observation !== 'object' || Array.isArray(observation)) {
        return { observed: false, reason: 'the write receipt carried no post-hook observation' };
    }
    const entries = Array.isArray(observation.documents) ? observation.documents : null;
    if (!entries) {
        return { observed: false, reason: 'the write receipt carried no path-keyed post-hook documents' };
    }
    const byPath = new Map();
    for (const entry of entries) {
        if (!entry || typeof entry.path !== 'string')
            continue;
        const document = entry.document;
        byPath.set(normalizePathKey(entry.path), (document && typeof document === 'object' && !Array.isArray(document))
            ? document
            : null);
    }
    const documents = [];
    const missing = [];
    for (const path of paths) {
        const key = normalizePathKey(path);
        if (!byPath.has(key)) {
            missing.push(path);
            documents.push({ path, document: null });
            continue;
        }
        documents.push({ path, document: (_a = byPath.get(key)) !== null && _a !== void 0 ? _a : null });
    }
    if (observation.observed !== true || missing.length > 0) {
        const unobserved = Array.isArray(observation.unobserved) ? observation.unobserved : [];
        const reasons = [
            ...unobserved
                .filter((entry) => entry && typeof entry.path === 'string')
                .map((entry) => { var _a; return `${entry.path}: ${(_a = entry.reason) !== null && _a !== void 0 ? _a : 'not observable'}`; }),
            ...missing.map(path => `${path}: no entry in the receipt observation`),
        ];
        return {
            observed: false,
            reason: reasons.length > 0
                ? reasons.join('; ')
                : 'the worker could not read every requested path back after hooks',
        };
    }
    return { observed: true, documents };
}
/**
 * Write a document at `path`. Sugar for a one-element {@link setMany}.
 *
 * **Delete:** pass `null` as the document to delete it — `set(path, null)` is
 * the delete (there is no separate `del`/`remove`). It is routed through the
 * collection's policy `delete` rule and broadcasts a delete to subscribers, and
 * the deleted entry surfaces as a `null` document under its path.
 *
 * Resolves to a discriminated {@link SetResult}; discriminate on `status`.
 */
export async function set(path, document, options) {
    // setMany already invalidates the read cache for the affected document and
    // collection paths — no second sweep needed here.
    return setMany([{ path, document }], options);
}
/**
 * Atomically write (or delete, with a `null` document) a batch of documents.
 *
 * Resolves to a discriminated {@link SetResult}:
 * - `committed` — the worker applied the batch directly.
 * - `confirmed` — a client-signed chain transaction landed successfully.
 * - `submitted` — broadcast happened but the outcome is unknown. NEVER
 *   resubmit; call `reconcileSetResult`/`waitForSetResult` on the receipt.
 * - `expired_not_landed` — Solana only, and the one retry-safe terminal state.
 * - `signed` — `shouldSubmitTx: false`; signed and deliberately not sent.
 *
 * `requestedDocuments` is always the caller's own echo; authoritative server
 * state appears only under `observation`/`mirrorSync` once actually observed.
 */
export async function setMany(many, options) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    // Returns the data that was set, or undefined if the document was already set.
    try {
        // A server WalletClient threads its immutable config snapshot; the default
        // path reads the mutable process-global init() config. This keeps the app-id
        // bound into an on-chain 202 write (Solana/offchain) on the wallet
        // client's original app after a later global init() (CTA-05).
        const config = (_b = (_a = options === null || options === void 0 ? void 0 : options._overrides) === null || _a === void 0 ? void 0 : _a._config) !== null && _b !== void 0 ? _b : await getConfig();
        const authProvider = (_d = (_c = options === null || options === void 0 ? void 0 : options._overrides) === null || _c === void 0 ? void 0 : _c.authProvider) !== null && _d !== void 0 ? _d : config.authProvider;
        if (!authProvider) {
            throw new Error('Auth provider not initialized');
        }
        let documents = [];
        const pathsToInvalidate = new Set();
        for (const { path, document: documentWithReservedFields } of many) {
            // for setting, we should remove any pieces of the document that start with '_' as they are internal and shouldn't be updated. The API would block it if it is one of the reserved ones
            let document = documentWithReservedFields;
            if (documentWithReservedFields) {
                document = Object.fromEntries(Object.entries(documentWithReservedFields).filter(([key]) => !key.startsWith('_')));
            }
            const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
            pathsToInvalidate.add(normalizedPath);
            const pathIsDocument = normalizedPath.split("/").length % 2 === 0;
            let curItemId = "";
            if (pathIsDocument) {
                curItemId = normalizedPath.split("/").pop() ? normalizedPath.split("/").pop() : "";
            }
            else {
                curItemId = Math.random().toString(36).slice(2).substring(0, 12);
            }
            // if the path is a document, we need to append the document id to the path
            const collectionPath = pathIsDocument ? normalizedPath.split("/").slice(0, -1).join("/") : normalizedPath;
            pathsToInvalidate.add(collectionPath);
            let destinationPath = collectionPath;
            if (curItemId && curItemId.length > 0) {
                destinationPath = `${destinationPath}/${curItemId}`;
            }
            documents.push({ destinationPath, document });
        }
        let setResponse;
        try {
            // SIMD-0385: tell the worker which Solana transaction formats this client
            // can sign, so it builds v1 only for a client that can complete it.
            const clientTransactionVersions = await resolveClientTransactionVersions(authProvider);
            setResponse = await makeApiRequest('PUT', `items`, { documents, clientTransactionVersions }, options === null || options === void 0 ? void 0 : options._overrides);
        }
        catch (error) {
            if ((error === null || error === void 0 ? void 0 : error.statusCode) === 402 && (error === null || error === void 0 ? void 0 : error.error) === 'INSUFFICIENT_BALANCE') {
                const deficitLamports = Number((_e = error.deficitLamports) !== null && _e !== void 0 ? _e : 0);
                const deficitSol = Number((_f = error.deficitSol) !== null && _f !== void 0 ? _f : deficitLamports / 1000000000);
                throw new InsufficientBalanceError(String((_g = error.address) !== null && _g !== void 0 ? _g : ''), Number((_h = error.balanceLamports) !== null && _h !== void 0 ? _h : 0), Number((_j = error.estimatedCostLamports) !== null && _j !== void 0 ? _j : 0), deficitLamports, deficitSol);
            }
            throw error;
        }
        // Invalidate caches and pending requests for affected paths
        pathsToInvalidate.forEach(path => {
            clearCacheByPrefix(path);
        });
        if (setResponse.status === 202) {
            // This means that the document needs to be set on-chain (or signed for offchain),
            // the API did nothing and gave us the required info to run this transaction.
            const { transactions, offchainTransaction } = setResponse.data;
            // Handle offchain transaction flow (for 'offchain' protocol apps)
            if (offchainTransaction && Array.isArray(transactions) && transactions.length > 0) {
                throw new Error('Ambiguous write response mixes offchain and onchain transaction targets');
            }
            if (offchainTransaction) {
                // Offchain signing is retired: no server mints this envelope any more
                // (the realtime worker commits poofnet writes directly). Fail loudly
                // rather than silently ignoring a payload the caller was asked to sign.
                throw new Error('Unsupported 202 transaction shape: offchain signing is retired');
            }
            // Handle Solana on-chain transaction flow
            if (!Array.isArray(transactions) || transactions.length !== 1) {
                throw new Error(`Expected exactly one on-chain transaction, received ${Array.isArray(transactions) ? transactions.length : 0}`);
            }
            const curTx = transactions[0];
            // The EVM onchain data plane is retired: no worker mints an EVM 202 any
            // more. A payload that still carries one is an unexpected response and
            // fails loudly BEFORE anything is signed - never silently ignored.
            if ((curTx === null || curTx === void 0 ? void 0 : curTx.evmTransaction) != null) {
                throw new Error('Unsupported 202 transaction shape: the write response carried an evmTransaction, ' +
                    'but EVM onchain writes are no longer supported. Nothing was signed or submitted.');
            }
            const hasSerializedSolanaTarget = (curTx === null || curTx === void 0 ? void 0 : curTx.serializedTransaction) != null;
            const hasBuiltSolanaTarget = (curTx === null || curTx === void 0 ? void 0 : curTx.transactionArgs) != null || (curTx === null || curTx === void 0 ? void 0 : curTx.remainingAccounts) != null ||
                (curTx === null || curTx === void 0 ? void 0 : curTx.idl) != null || (curTx === null || curTx === void 0 ? void 0 : curTx.txData) != null || (curTx === null || curTx === void 0 ? void 0 : curTx.preInstructions) != null;
            if (hasSerializedSolanaTarget && hasBuiltSolanaTarget) {
                throw new Error('Ambiguous on-chain write response mixes Solana transaction target formats');
            }
            // All requested paths (writes AND deletes), in request order. Used for the
            // post-confirm mirror read-back, which correlates every requested path.
            // The pre-sign validation derives its own full expected intent from
            // `documents` (see below) and needs nothing from this list.
            const requestedPaths = documents.map(d => d.destinationPath);
            const requestedDocuments = buildRequestedDocuments(documents);
            const appId = config.appId;
            // The lane comes verbatim from the worker, and is read BEFORE anything is
            // signed or submitted: a missing tag fails closed while nothing has been
            // broadcast, instead of stranding a landed transaction with no receipt.
            const workerChain = readWorkerChainTag((_k = setResponse.data) === null || _k === void 0 ? void 0 : _k.receipt, setResponse.data, curTx);
            if (!workerChain) {
                throw new Error('The write response carried no execution-chain tag, so this receipt could not state where the write ran. ' +
                    'Nothing was signed or submitted.');
            }
            if (curTx.serializedTransaction) {
                if (!SOLANA_CHAIN_TAGS.has(workerChain)) {
                    throw new Error(`The write response reported execution chain "${workerChain}" for a Solana transaction. ` +
                        'Nothing was signed or submitted.');
                }
                const solanaChain = workerChain;
                const { handlePreBuiltTransaction, buildExpectedWriteIntent } = await import('./solana-operations');
                // OA-0001: derive the expected intent from OUR OWN request (the one
                // ground truth a tampered builder response cannot change) so the
                // validator can reject any transaction whose operation semantics
                // diverge from it BEFORE the wallet is asked to sign.
                const expectedIntent = buildExpectedWriteIntent(documents);
                const solanaOutcome = await handlePreBuiltTransaction(curTx, authProvider, options, expectedIntent);
                // `shouldSubmitTx: false` signed WITHOUT sending — no signature exists,
                // and there is nothing to mirror, so no synchronization is attempted.
                if (solanaOutcome.outcome === 'signed') {
                    return {
                        status: 'signed', appId, requestedDocuments, chain: solanaChain,
                        transactionId: null,
                        signedTransaction: solanaOutcome.signedTransaction,
                        fence: solanaOutcome.fence,
                    };
                }
                if (solanaOutcome.outcome === 'submitted') {
                    return {
                        status: 'submitted', appId, requestedDocuments, chain: solanaChain,
                        transactionId: solanaOutcome.transactionSignature,
                        signedTransaction: solanaOutcome.signedTransaction,
                        fence: solanaOutcome.fence,
                        confirmation: { state: 'unknown', reason: solanaOutcome.reason },
                    };
                }
                // Confirmed: the receipt is already complete, so the mirror read is a
                // best-effort enrichment reported in `mirrorSync`, never a rejection.
                const mirrorSync = await attemptMirrorSync(requestedPaths, options);
                return {
                    status: 'confirmed', appId, requestedDocuments, chain: solanaChain,
                    transactionId: solanaOutcome.transactionSignature,
                    signedTransaction: solanaOutcome.signedTransaction,
                    fence: solanaOutcome.fence,
                    confirmationContextSlot: solanaOutcome.confirmationContextSlot,
                    mirrorSync,
                };
            }
            else {
                throw new Error('Unsupported 202 transaction shape: expected serializedTransaction');
            }
        }
        else if (setResponse.status === 200) {
            // Direct commit: the worker applied the batch itself. The receipt is
            // batch-level — `transactionId` comes from the worker's own receipt and is
            // NEVER lifted out of a result document (a delete returns the PREVIOUS
            // document, so its stale `_transaction_hash` would misreport the delete).
            const body = (setResponse.data && typeof setResponse.data === 'object' && !Array.isArray(setResponse.data))
                ? setResponse.data
                : null;
            const receipt = ((body === null || body === void 0 ? void 0 : body.receipt) && typeof body.receipt === 'object' && !Array.isArray(body.receipt))
                ? body.receipt
                : null;
            const rawTransactionId = receipt === null || receipt === void 0 ? void 0 : receipt.transactionId;
            return {
                status: 'committed',
                requestedDocuments: buildRequestedDocuments(documents),
                observation: observationFromDirectCommit(documents.map(d => d.destinationPath), receipt === null || receipt === void 0 ? void 0 : receipt.observation),
                // A worker predating the batch receipt reports no lane. Say so rather
                // than inventing one — and never discard a committed write's receipt.
                chain: ((_l = readWorkerChainTag(receipt)) !== null && _l !== void 0 ? _l : 'unknown'),
                transactionId: typeof rawTransactionId === 'string' && rawTransactionId.length > 0 ? rawTransactionId : null,
            };
        }
        else {
            throw new Error(`Failed to set document: ${setResponse}`);
        }
    }
    catch (error) {
        throw error;
    }
}
/**
 * Extract the document/collection path from a cache key. Keys are
 * `<appId:principal>|<path>:<...>` (H1 principal-scoped) — strip the principal
 * prefix (everything up to and including the first `|`, if present) before
 * splitting off the path segment.
 */
function pathFromCacheKey(key) {
    const barIdx = key.indexOf('|');
    const afterPrincipal = barIdx >= 0 ? key.slice(barIdx + 1) : key;
    return afterPrincipal.split(':')[0];
}
// Helper to clear cache entries by prefix (across ALL principals — a write
// invalidates every cached read of that path regardless of which user cached it)
function clearCacheByPrefix(prefix) {
    // Fixed: Use prefix instead of undefined 'path' variable
    const normalizedPrefix = prefix.startsWith("/") ? prefix.slice(1) : prefix;
    // Clear cache entries
    Object.keys(getCache).forEach(key => {
        const path = pathFromCacheKey(key);
        if (path === normalizedPrefix || path.startsWith(`${normalizedPrefix}/`)) {
            delete getCache[key];
        }
    });
    // Cancel pending requests for this path
    Object.keys(pendingRequests).forEach(key => {
        // Skip bypass-specific requests which have a different format
        const lookupKey = key.startsWith('bypass:') ? key.slice('bypass:'.length) : key;
        const path = pathFromCacheKey(lookupKey);
        if (path === normalizedPrefix || path.startsWith(`${normalizedPrefix}/`)) {
            // We don't actually abort the fetch (we could with AbortController),
            // but we remove it from pending so new requests won't coalesce with it
            delete pendingRequests[key];
        }
    });
}
// Utility functions for cache management
export function clearCache(path, opts) {
    if (path) {
        // Clear cache entries for this path across ALL principals. Keys are now
        // principal-scoped (`<appId:principal>|<path>:<...>`), so we can't compute a
        // single deterministic key — match by the path segment instead. When a
        // prompt is supplied, only drop entries for that exact prompt.
        const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
        const prompt = (opts === null || opts === void 0 ? void 0 : opts.prompt) || '';
        const matches = (key) => {
            const barIdx = key.indexOf('|');
            const afterPrincipal = barIdx >= 0 ? key.slice(barIdx + 1) : key;
            const colonIdx = afterPrincipal.indexOf(':');
            if (colonIdx < 0)
                return false;
            const keyPath = afterPrincipal.slice(0, colonIdx);
            if (keyPath !== normalizedPath)
                return false;
            if (!(opts === null || opts === void 0 ? void 0 : opts.prompt))
                return true;
            // The prompt is the segment immediately after the path's first colon.
            const rest = afterPrincipal.slice(colonIdx + 1);
            return rest === prompt || rest.startsWith(`${prompt}:`) || rest.startsWith(prompt);
        };
        Object.keys(getCache).forEach(key => { if (matches(key))
            delete getCache[key]; });
        Object.keys(pendingRequests).forEach(key => {
            const lookupKey = key.startsWith('bypass:') ? key.slice('bypass:'.length) : key;
            if (matches(lookupKey))
                delete pendingRequests[key];
        });
    }
    else {
        // Clear entire cache
        Object.keys(getCache).forEach(key => delete getCache[key]);
        Object.keys(pendingRequests).forEach(key => delete pendingRequests[key]);
    }
}
/**
 * Listeners notified after an identity change wipes the read caches. Registered
 * via `onReadCacheAuthChange` so downstream modules (e.g. the subscribeQuery poll
 * manager) can re-key/drop stale in-flight work WITHOUT operations.ts importing
 * them (avoids a static cycle; keeps the coalesce test bundle lean).
 */
const authChangeListeners = new Set();
/** Register a callback invoked after `clearReadCacheForAuthChange`. Returns an unregister fn. */
export function onReadCacheAuthChange(listener) {
    authChangeListeners.add(listener);
    return () => { authChangeListeners.delete(listener); };
}
/**
 * SECURITY (H1): Wipe ALL HTTP read caches + in-flight reads. Call this whenever
 * the logged-in identity changes (login / logout / switch identity) so a freshly
 * authenticated principal can never observe data cached for the previous one.
 * This is invoked from the WS auth-change path (reconnectWithNewAuthV2).
 */
export function clearReadCacheForAuthChange() {
    Object.keys(getCache).forEach(key => delete getCache[key]);
    Object.keys(pendingRequests).forEach(key => delete pendingRequests[key]);
    authChangeListeners.forEach(fn => {
        try {
            fn();
        }
        catch ( /* a listener throwing must not abort the cache wipe */_a) { /* a listener throwing must not abort the cache wipe */ }
    });
}
export async function getFiles(path, options) {
    try {
        const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
        if (!normalizedPath || normalizedPath.length === 0) {
            throw new Error("Invalid path provided.");
        }
        const apiPath = `storage?path=${encodeURIComponent(normalizedPath)}`;
        const response = await makeApiRequest('GET', apiPath, null, options === null || options === void 0 ? void 0 : options._overrides);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}
/** Pull the worker's concrete object path (`token.pathId`) out of a response body. */
function readWorkerFilePath(source) {
    if (!source || typeof source !== 'object')
        return null;
    for (const key of ['pathId', 'path', 'destinationPath']) {
        const value = source[key];
        if (typeof value === 'string' && value.trim().length > 0)
            return value.trim();
    }
    return null;
}
function readFileVisibility(source) {
    const value = (source && typeof source === 'object') ? source.visibility : undefined;
    if (value === 'public' || value === 'private' || value === 'unknown')
        return value;
    // The worker did not state it, so neither do we. `unknown` never means the
    // object is private — it means the read-rule evaluation was not reported.
    return 'unknown';
}
export async function setFile(path, file, options) {
    var _a, _b, _c, _d;
    // 1) Get the presigned URL from your backend
    const requestBody = {
        destinationPath: path,
        operation: file ? 'upload' : 'delete',
    };
    // Send file size so the server can enforce upload limits before issuing a presigned URL
    if (file) {
        requestBody.contentLength = file.size;
        requestBody.contentType = file.type || undefined;
        // Declared metadata fields set atomically with the create (server validates
        // them against the storage collection's `fields` and applies the CREATE rule).
        if (options === null || options === void 0 ? void 0 : options.metadata) {
            requestBody.metadata = options.metadata;
        }
    }
    const response = await makeApiRequest('POST', 'storage/url', requestBody, options === null || options === void 0 ? void 0 : options._overrides);
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    if (file == null) {
        // A deletion is acknowledged only by a non-404 response. `makeApiRequest`
        // normalizes 404 into `{ data: null, status: 404 }` rather than throwing, so
        // the status has to be inspected.
        const deletePath = (_a = readWorkerFilePath(response === null || response === void 0 ? void 0 : response.data)) !== null && _a !== void 0 ? _a : normalizedPath;
        if ((response === null || response === void 0 ? void 0 : response.status) === 404) {
            return { path: deletePath, deleted: false, alreadyAbsent: true };
        }
        return { path: deletePath, deleted: true };
    }
    if (!((_b = response === null || response === void 0 ? void 0 : response.data) === null || _b === void 0 ? void 0 : _b.s3Url)) {
        throw new Error("No s3Url in response");
    }
    // Client-side size guard using the limit returned by the server
    const maxFileSize = response.data.maxFileSize;
    if (maxFileSize && file.size > maxFileSize) {
        throw new Error(`File size ${(file.size / (1024 * 1024)).toFixed(2)} MB exceeds the maximum upload size of ${(maxFileSize / (1024 * 1024)).toFixed(0)} MB.`);
    }
    const presignedUrl = response.data.s3Url;
    // Retain the CONCRETE path BEFORE the PUT. Storage generates a collection
    // upload's id server-side, so `setFile("metadata", file)` creates
    // `metadata/<generated-id>`; if the commit succeeds and only its response is
    // lost, this is the only identifier the object has.
    const retainedPath = (_c = readWorkerFilePath(response.data)) !== null && _c !== void 0 ? _c : normalizedPath;
    // 2) Upload the file directly to storage with a PUT request
    let uploadResponse;
    try {
        uploadResponse = await fetch(presignedUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': file.type,
            },
            body: file
        });
    }
    catch (error) {
        // The transport itself failed, so we do not know whether the object
        // committed. This is NOT a definite failure and NOT retry-safe.
        const message = typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' ? error.message : String(error);
        return {
            status: 'unknown',
            path: retainedPath,
            reason: `the upload response was lost before it could be read: ${message}`,
            retrySafe: false,
        };
    }
    // 3) A non-2xx answer is a definite, unambiguous failure — keep throwing.
    if (!uploadResponse.ok) {
        await uploadResponse.text().catch(() => '');
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
    }
    // 4) The PUT response IS the finalize response: it carries the durable URL
    //    and the anonymous-read verdict, inside the worker's `{ data, status }`
    //    envelope.
    let finalizeBody = null;
    try {
        const parsed = await uploadResponse.json();
        finalizeBody = (parsed && typeof parsed === 'object' && parsed.data && typeof parsed.data === 'object')
            ? parsed.data
            : parsed;
    }
    catch (_e) {
        // A worker predating the URL-carrying finalize response answers with
        // something we cannot parse. The object still committed.
        finalizeBody = null;
    }
    const url = (finalizeBody && typeof finalizeBody.url === 'string' && finalizeBody.url.length > 0)
        ? finalizeBody.url
        : null;
    return {
        status: 'uploaded',
        path: (_d = readWorkerFilePath(finalizeBody)) !== null && _d !== void 0 ? _d : retainedPath,
        url,
        visibility: readFileVisibility(finalizeBody),
    };
}
export async function signMessage(message) {
    const config = await getConfig();
    if (!config.authProvider) {
        throw new Error('Auth provider not initialized. Please call init() first.');
    }
    return config.authProvider.signMessage(message);
}
export async function signTransaction(transaction) {
    const config = await getConfig();
    if (!config.authProvider) {
        throw new Error('Auth provider not initialized. Please call init() first.');
    }
    return config.authProvider.signTransaction(transaction);
}
export async function signAndSubmitTransaction(transaction, feePayer) {
    const config = await getConfig();
    if (!config.authProvider) {
        throw new Error('Auth provider not initialized. Please call init() first.');
    }
    return config.authProvider.signAndSubmitTransaction(transaction, feePayer);
}
export async function syncItems(paths, options) {
    try {
        const response = await makeApiRequest('PUT', 'items/sync', { paths }, options === null || options === void 0 ? void 0 : options._overrides);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}
